import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Eye, 
  Edit, 
  Package,
  Activity,
  AlertCircle,
  Tag,
  Clock,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import { productosApi, variablesProductosApi, Producto, TipoProducto } from '../../apis/productosDinamicosApi';
import CrearProducto from './CrearProducto';
import EditarProducto from './EditarProducto';
import VerProducto from './VerProducto';

// Interface para estadísticas de productos
interface ProductosEstadisticas {
  total_productos: number;
  productos_activos: number;
  productos_con_tag: number;
  productos_sin_tag: number;
}

const ProductosDinamicos: React.FC = () => {
  const authContext = useContext(AuthContext);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [allProductos, setAllProductos] = useState<Producto[]>([]);
  const [filteredProductos, setFilteredProductos] = useState<Producto[]>([]);
  const [tiposProductos, setTiposProductos] = useState<TipoProducto[]>([]);
  const [estadisticas, setEstadisticas] = useState<ProductosEstadisticas | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTipo, setSelectedTipo] = useState<number | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProducto, setSelectedProducto] = useState<Producto | null>(null);

  const itemsPerPage = 15;
  
  // Verificar si el usuario es de Skyangel (plataforma_id = 4)
  const isSkyangel = authContext.usuario?.plataforma_id === 4;

  // Cargar datos iniciales
  useEffect(() => {
    loadProductos();
    loadTiposProductos();
  }, []);

  // Filtrar productos cuando cambie el término de búsqueda o tipo seleccionado
  useEffect(() => {
    filterProductos();
  }, [searchTerm, selectedTipo, allProductos]);

  // Paginar los productos filtrados
  useEffect(() => {
    paginateFilteredProductos();
  }, [filteredProductos, currentPage]);

  const loadProductos = async () => {
    try {
      setLoading(true);
      const response = await productosApi.getAll({ per_page: 1000 }); // Cargar todos para estadísticas
      
      if (response && response.data && Array.isArray(response.data)) {
        setAllProductos(response.data);
        calculateEstadisticas(response.data);
      }
    } catch (error: any) {
      console.error('Error cargando productos:', error);
      if (error.response?.status !== 403) {
        toast.error('Error al cargar productos');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTiposProductos = async () => {
    try {
      const tipos = await variablesProductosApi.getTipos();
      setTiposProductos(tipos);
    } catch (error) {
      console.error('Error cargando tipos de productos:', error);
    }
  };

  const calculateEstadisticas = (productosData: Producto[]) => {
    const stats: ProductosEstadisticas = {
      total_productos: productosData.length,
      productos_activos: productosData.filter(p => p.activo).length,
      productos_con_tag: productosData.filter(p => p.tagblac_id !== null).length,
      productos_sin_tag: productosData.filter(p => p.tagblac_id === null).length
    };
    setEstadisticas(stats);
  };

  const filterProductos = () => {
    let filtered = [...allProductos];

    // Filtrar por tipo de producto
    if (selectedTipo) {
      filtered = filtered.filter(producto => producto.variable_producto_id === selectedTipo);
    }

    // Filtrar por término de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(producto => {
        const searchableText = [
          producto.tipo_producto,
          ...producto.datos,
          producto.tag_blac?.plate || '',
          producto.tag_blac?.name_device || ''
        ].join(' ').toLowerCase();
        
        return searchableText.includes(term);
      });
    }

    setFilteredProductos(filtered);
    setCurrentPage(1); // Reset a la primera página cuando se filtra
  };

  const paginateFilteredProductos = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedProductos = filteredProductos.slice(startIndex, endIndex);
    
    setProductos(paginatedProductos);
    setTotalPages(Math.ceil(filteredProductos.length / itemsPerPage));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleTipoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTipo(e.target.value === '' ? '' : Number(e.target.value));
  };

  const handleRefresh = async () => {
    await loadProductos();
    await loadTiposProductos();
  };

  const handleEdit = (producto: Producto) => {
    setSelectedProducto(producto);
    setShowEditModal(true);
  };

  const handleView = (producto: Producto) => {
    setSelectedProducto(producto);
    setShowViewModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este producto?')) {
      try {
        await productosApi.eliminar(id);
        toast.success('Producto eliminado exitosamente');
        await loadProductos();
      } catch (error) {
        console.error('Error eliminando producto:', error);
        toast.error('Error al eliminar producto');
      }
    }
  };

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    try {
      return new Date(dateTime).toLocaleString('es-MX');
    } catch {
      return dateTime;
    }
  };

  const getStatusColor = (activo: boolean) => {
    return activo 
      ? 'text-green-400 bg-green-400/10'
      : 'text-red-400 bg-red-400/10';
  };

  const styles = `
    .productos-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .content-wrapper {
      max-width: 1400px;
      margin: 0 auto;
      padding: 2rem;
    }

    .header-section {
      margin-bottom: 2rem;
    }

    .page-title {
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 0.5rem;
    }

    .page-subtitle {
      color: rgba(255, 255, 255, 0.7);
      font-size: 1.1rem;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .stat-card {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 1.5rem;
      transition: all 0.3s ease;
    }

    .stat-card:hover {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(0, 122, 255, 0.3);
      transform: translateY(-2px);
    }

    .stat-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    .stat-title {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.7);
      font-weight: 500;
    }

    .stat-value {
      font-size: 2rem;
      font-weight: 700;
      color: white;
      line-height: 1;
    }

    .controls-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      margin-bottom: 2rem;
      flex-wrap: wrap;
    }

    .search-container {
      position: relative;
      flex: 1;
      max-width: 400px;
    }

    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(255, 255, 255, 0.5);
    }

    .search-input {
      width: 100%;
      padding: 12px 12px 12px 44px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      color: white;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .search-input:focus {
      outline: none;
      background: rgba(255, 255, 255, 0.15);
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
    }

    .search-input::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }

    .filters-section {
      display: flex;
      gap: 1rem;
      align-items: center;
    }

    .filter-select {
      padding: 12px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 12px;
      color: white;
      font-size: 14px;
      min-width: 150px;
    }

    .filter-select option {
      background: #1a1a1a;
      color: white;
    }

    .action-buttons {
      display: flex;
      gap: 1rem;
      align-items: center;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px;
      border-radius: 12px;
      font-weight: 500;
      font-size: 14px;
      text-decoration: none;
      transition: all 0.3s ease;
      cursor: pointer;
      border: none;
    }

    .btn-primary {
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      color: white;
      box-shadow: 0 4px 15px rgba(0, 122, 255, 0.3);
    }

    .btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 122, 255, 0.4);
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
    }

    .btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .btn-small {
      padding: 6px 12px;
      font-size: 12px;
    }

    .table-container {
      background: rgba(255, 255, 255, 0.05);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      overflow: hidden;
    }

    .table {
      width: 100%;
      border-collapse: collapse;
    }

    .table th {
      background: rgba(255, 255, 255, 0.1);
      padding: 1rem;
      text-align: left;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .table td {
      padding: 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.8);
    }

    .table tr:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }

    .tag-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .tag-name {
      font-weight: 500;
      color: #007AFF;
    }

    .tag-plate {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.6);
      font-family: 'Courier New', monospace;
    }

    .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 1rem;
      margin-top: 2rem;
      padding: 1rem;
    }

    .pagination-btn {
      padding: 8px 16px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .pagination-btn:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.15);
    }

    .pagination-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .loading-spinner {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 4rem;
    }

    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid rgba(255, 255, 255, 0.1);
      border-top: 3px solid #007AFF;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .empty-state {
      text-align: center;
      padding: 4rem 2rem;
      color: rgba(255, 255, 255, 0.6);
    }

    .empty-icon {
      margin: 0 auto 1rem;
      opacity: 0.5;
    }

    .search-results-indicator {
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .producto-data {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .producto-data-item {
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.7);
    }

    .producto-data-label {
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
    }

    @media (max-width: 768px) {
      .content-wrapper {
        padding: 1rem;
      }

      .page-title {
        font-size: 2rem;
      }

      .controls-section {
        flex-direction: column;
        align-items: stretch;
      }

      .search-container {
        max-width: none;
      }

      .action-buttons {
        justify-content: center;
      }

      .table-container {
        overflow-x: auto;
      }

      .table {
        min-width: 900px;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="productos-container">
        <div className="content-wrapper">
          {/* Header */}
          <div className="header-section">
            <h1 className="page-title">Productos Dinámicos</h1>
            <p className="page-subtitle">
              Gestión de productos con sistema dinámico de variables
            </p>
          </div>

          {/* Estadísticas */}
          {estadisticas && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Total</span>
                  <Package size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.total_productos}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginTop: '0.5rem' }}>
                  {searchTerm || selectedTipo ? `${filteredProductos.length} mostrados` : 'Productos'}
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Activos</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10B981' }} />
                </div>
                <div className="stat-value">{estadisticas.productos_activos}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Con Tag</span>
                  <Tag size={20} color="#007AFF" />
                </div>
                <div className="stat-value">{estadisticas.productos_con_tag}</div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <span className="stat-title">Sin Tag</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F59E0B' }} />
                </div>
                <div className="stat-value">{estadisticas.productos_sin_tag}</div>
              </div>
            </div>
          )}

          {/* Controles */}
          <div className="controls-section">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Buscar por tipo, datos del producto o tag..."
                value={searchTerm}
                onChange={handleSearch}
                className="search-input"
              />
            </div>

            <div className="filters-section">
              <select
                value={selectedTipo}
                onChange={handleTipoChange}
                className="filter-select"
              >
                <option value="">Todos los tipos</option>
                {tiposProductos.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.producto}
                  </option>
                ))}
              </select>
            </div>

            <div className="action-buttons">
              <button
                onClick={handleRefresh}
                className="btn btn-secondary"
              >
                <RefreshCw size={16} />
                Actualizar
              </button>

              {authContext.tienePermiso('productos.crear') && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn btn-primary"
                >
                  <Plus size={16} />
                  Nuevo Producto
                </button>
              )}
            </div>
          </div>

          {/* Indicador de resultados */}
          {(searchTerm || selectedTipo) && (
            <div className="search-results-indicator" style={{
              padding: '1rem 2rem',
              background: 'rgba(0, 122, 255, 0.1)',
              border: '1px solid rgba(0, 122, 255, 0.2)',
              borderRadius: '12px',
              marginBottom: '1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.9rem'
            }}>
              🔍 Mostrando {filteredProductos.length} resultado{filteredProductos.length !== 1 ? 's' : ''} 
              {searchTerm && ` para "${searchTerm}"`}
              {selectedTipo && ` del tipo "${tiposProductos.find(t => t.id === selectedTipo)?.producto}"`}
            </div>
          )}

          {/* Tabla de Productos */}
          <div className="table-container">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner" />
              </div>
            ) : productos.length === 0 ? (
              <div className="empty-state">
                <Package className="empty-icon" size={48} />
                <h3>
                  {searchTerm || selectedTipo ? 'No se encontraron resultados' : 'No hay productos registrados'}
                </h3>
                <p>
                  {searchTerm || selectedTipo
                    ? 'No hay productos que coincidan con los filtros aplicados'
                    : 'Comienza creando tu primer producto dinámico'
                  }
                </p>
                {!searchTerm && !selectedTipo && authContext.tienePermiso('productos.crear') && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary"
                    style={{ marginTop: '1rem' }}
                  >
                    <Plus size={16} />
                    Crear Primer Producto
                  </button>
                )}
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tipo</th>
                    <th>Datos del Producto</th>
                    <th>Tag</th>
                    {isSkyangel && <th>Plataforma</th>}
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.map((producto) => (
                    <tr key={producto.id}>
                      <td>#{producto.id}</td>
                      <td>
                        <div style={{ fontWeight: 500, color: '#007AFF' }}>
                          {producto.tipo_producto}
                        </div>
                      </td>
                      <td>
                        <div className="producto-data">
                          {Object.entries(producto.datos_mapeados || {}).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="producto-data-item">
                              <span className="producto-data-label">{key}:</span> {value}
                            </div>
                          ))}
                          {Object.keys(producto.datos_mapeados || {}).length > 3 && (
                            <div className="producto-data-item" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                              +{Object.keys(producto.datos_mapeados || {}).length - 3} más...
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {producto.tag_blac ? (
                          <div className="tag-info">
                            <div className="tag-name">{producto.tag_blac.name_device}</div>
                            <div className="tag-plate">{producto.tag_blac.plate}</div>
                          </div>
                        ) : producto.tag_tracksolid ? (
                          <div className="tag-info">
                            <div className="tag-name">{producto.tag_tracksolid.device_name || producto.tag_tracksolid.imei}</div>
                            <div className="tag-plate">{producto.tag_tracksolid.plate || producto.tag_tracksolid.imei}</div>
                          </div>
                        ) : (
                          <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Sin tag asignada</span>
                        )}
                      </td>
                      {isSkyangel && (
                        <td>
                          <span className="badge badge-green">
                            {producto.plataforma_nombre || 'N/A'}
                          </span>
                        </td>
                      )}
                      <td>
                        <span className={`status-badge ${getStatusColor(producto.activo)}`}>
                          <div style={{ 
                            width: 6, 
                            height: 6, 
                            borderRadius: '50%', 
                            backgroundColor: producto.activo ? '#10B981' : '#EF4444' 
                          }} />
                          {producto.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>{formatDateTime(producto.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleView(producto)}
                            className="btn btn-secondary btn-small"
                            title="Ver detalles"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => handleEdit(producto)}
                            className="btn btn-secondary btn-small"
                            title="Editar"
                            style={{ display: authContext.tienePermiso('productos.editar') ? undefined : 'none' }}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(producto.id)}
                            className="btn btn-secondary btn-small"
                            title="Eliminar"
                            style={{ color: '#EF4444', display: authContext.tienePermiso('productos.eliminar') ? undefined : 'none' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Anterior
              </button>
              
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Página {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>

        {/* Modales */}
        {showCreateModal && (
          <CrearProducto
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              loadProductos();
            }}
          />
        )}

        {showEditModal && selectedProducto && (
          <EditarProducto
            producto={selectedProducto}
            onClose={() => {
              setShowEditModal(false);
              setSelectedProducto(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedProducto(null);
              loadProductos();
            }}
          />
        )}

        {showViewModal && selectedProducto && (
          <VerProducto
            producto={selectedProducto}
            onClose={() => {
              setShowViewModal(false);
              setSelectedProducto(null);
            }}
          />
        )}
      </div>
    </>
  );
};

export default ProductosDinamicos;
